import { useMemo, useRef } from "react";
import { CriticalPeriodBand } from "./useCriticalPeriods";
import { SecondaryColor } from "../../../../common";
import { LegendKey } from "../../../../models/chart_model";
import { normalizeDisplayLabel } from "../../../../utility/displayText";
import { LARGE_DATA_THRESHOLD } from "../constants";
import { formatAxisValue } from "../utils/formatUtils";
import { buildSecondXAxisConfig } from "./buildSecondXAxisConfig";
import { EChartsSeries } from "../types";

/**
 * Inputs for buildOptions. Grouped by concern below for readability.
 */
interface Params {
  // ---- data ----
  /** Memoized chart data rows. */
  debouncedData: any[];
  /** X-axis values pre-extracted from `debouncedData[xDataKey]`. */
  xAxisData: any[];
  /** Field on each row that drives the primary x-axis. */
  xDataKey: string;
  /** Optional second-axis date key (paints date row under primary x). */
  secondXDataKey?: string;

  // ---- legends + colors ----
  /** Legends sorted via compareLegends, filtered by selection. */
  orderedSelectedLegends: LegendKey[];
  /** Active selection state from the user. */
  selectedLegends: LegendKey[];
  /** Names hidden from rendering (e.g. summary-only metrics). */
  hiddenStats: string[];
  /** Stable color per `getLegendColorKey`. */
  colorMap: Record<string, string>;
  /** Per-bar palette when `colorByPoint` is on. */
  barColors?: string[];
  /** When true, bars draw their per-point color from `barColors`. */
  colorByPoint?: boolean;

  // ---- bar tuning ----
  /** Category gap between bars in percent. */
  barCategoryGap: number;
  /** True when chart belongs to the price-forecast view (bar coloring rule). */
  isPriceForecast: boolean;

  // ---- brush / zoom ----
  showBrush: boolean;
  brushHeight: number;
  brushIndex?: { startIndex?: number; endIndex?: number };
  enableMouseWheelZoom: boolean;
  /** Reactive zoom from useZoomState — drives 2nd x-axis label density. */
  zoomRange: { start: number; end: number } | null;
  /** Last zoom captured into ref — preserves zoom across legend toggles. */
  lastZoomRef: React.MutableRefObject<{ start: number; end: number } | null>;

  // ---- layout ----
  left?: number;
  bottom: number;
  top: number;
  xaxisHeight: number;
  isSmallLegend: boolean;

  // ---- tooltip ----
  customTooltip?: any;
  tooltipFormatter: any;
  /** Force tooltip to hide immediately when pointer exits chart area. */
  hideTooltipOnChartExit?: boolean;

  // ---- text / fonts ----
  fontSize: number;
  labelFontSize?: number;
  xLabel?: string;
  yAxisLabel?: string;
  secondYAxisLabel?: string;

  // ---- axes ----
  xAxisAngle?: number;
  xAxisTickMargin: number;
  xAxisLabelPosition: any;
  xAxisLabelOffset?: number;
  xAxisInterval?: number | "auto";
  yMaxValue?: number;
  nameGap?: number;
  secondYAxisDomain?: number[];

  // ---- misc ----
  showToolbox: boolean;
  /** Pulled into series builder via callback. */
  buildSeries: any;
  /** Enable invisible click-capture overlay. */
  enableClickSelection?: boolean;
  /** X-values to highlight via translucent block overlay. */
  highlightedXValues?: any[];
  /** Time-slot mode flips smart-interval behaviour to "auto". */
  isTimeSlot?: boolean;
  /** Tooltip clamp boundary. "viewport" (default) clamps to page; "chart" clamps x to chart width. */
  tooltipBoundary?: "viewport" | "chart";
  /** Returns chart container DOM rect in page coords. Needed because tooltip
   *  is body-appended in viewport mode and cannot find its chart from dom. */
  getChartRect?: () => DOMRect | null;
  /** Critical period bands — one per active kind. Each carries per-index fill data. */
  criticalPeriodBands?: CriticalPeriodBand[];
  /** Custom formatter for x-axis tick labels. Overrides default normalizeDisplayLabel. */
  xAxisTickFormatter?: (value: string) => string;
}

export function useChartOptions(p: Params) {
  const {
    debouncedData,
    orderedSelectedLegends,
    selectedLegends,
    hiddenStats,
    colorMap,
    barCategoryGap,
    isPriceForecast,
    left,
    showBrush,
    brushHeight,
    xaxisHeight,
    isSmallLegend,
    bottom,
    top,
    customTooltip,
    tooltipFormatter,
    fontSize,
    showToolbox,
    xAxisData,
    xAxisAngle,
    xAxisTickMargin,
    xLabel,
    xAxisLabelPosition,
    xAxisLabelOffset,
    labelFontSize,
    secondXDataKey,
    yMaxValue,
    yAxisLabel,
    nameGap,
    secondYAxisLabel,
    brushIndex,
    enableMouseWheelZoom,
    secondYAxisDomain,
    buildSeries,
    colorByPoint,
    barColors,
    enableClickSelection,
    highlightedXValues,
    xDataKey,
    xAxisInterval,
    isTimeSlot,
    hideTooltipOnChartExit = false,
    zoomRange,
    lastZoomRef,
    tooltipBoundary = "chart",
    getChartRect,
    criticalPeriodBands,
    xAxisTickFormatter,
  } = p;

  const lastOptionsRef = useRef<any>(null);
  // Persist last-known tooltip content size across notMerge reinits. ECharts
  // sometimes calls position() with contentSize=[0,0] right after reinit,
  // and the tooltip DOM is recreated so dom-attached caches are wiped.
  // A hook-level ref survives reinit → stable up/down flip decision.
  const tooltipSizeRef = useRef<{ cw: number; ch: number } | null>(null);
  const tooltipSideRef = useRef<"left" | "right" | undefined>(undefined);

  return useMemo(() => {
    if (!debouncedData || debouncedData.length === 0) {
      // Preserve last options so brush/axes don't flash off during reloads.
      // notMerge={true} on the chart would wipe dataZoom otherwise.
      // On fresh mount (no cached options yet) emit a minimal skeleton
      // containing dataZoom so the brush is present from frame 0.
      if (lastOptionsRef.current) return lastOptionsRef.current;
      if (!showBrush) return {};
      const skeletonZoomXAxisIndex = secondXDataKey ? [0, 1] : [0];
      const skeleton = {
        xAxis: secondXDataKey
          ? [
              { type: "category", data: [], show: false },
              { type: "category", data: [], show: false },
            ]
          : [{ type: "category", data: [], show: false }],
        yAxis: [
          { type: "value", show: false },
          { type: "value", position: "right", show: false },
        ],
        series: [],
        dataZoom: [
          {
            type: "slider",
            start: 0,
            end: 100,
            height: brushHeight,
            width: "98%",
            bottom: 10,
            left: 10,
            brushSelect: false,
            xAxisIndex: skeletonZoomXAxisIndex,
            textStyle: { color: SecondaryColor },
            handleIcon:
              "path://M10.7,11.9v-1.3H9.3v1.3c-4.9,0.3-8.8,4.4-8.8,9.4c0,5,3.9,9.1,8.8,9.4v1.3h1.3v-1.3c4.9-0.3,8.8-4.4,8.8-9.4C19.5,16.3,15.6,12.2,10.7,11.9z M13.3,24.4H6.7V23h6.6V24.4z M13.3,19.6H6.7v-1.4h6.6V19.6z",
            handleSize: "80%",
            filterMode: "none" as const,
          },
        ],
      };
      lastOptionsRef.current = skeleton;
      return skeleton;
    }

    const isLarge = debouncedData.length > LARGE_DATA_THRESHOLD;

    const series: EChartsSeries[] = buildSeries(
      debouncedData,
      orderedSelectedLegends,
      selectedLegends,
      hiddenStats,
      colorMap,
      barCategoryGap,
      isPriceForecast,
      colorByPoint,
      barColors,
    );

    // Compute maxValue once for both click & highlight layers
    let cachedMaxValue: number | null = null;
    const getMaxValue = () => {
      if (cachedMaxValue !== null) return cachedMaxValue;
      let max = 1000;
      const activeLegends = orderedSelectedLegends.filter(
        (leg) => !hiddenStats.includes(leg.name || ""),
      );
      // Sample at most 500 points for max calculation on large datasets
      const step = isLarge
        ? Math.max(1, Math.floor(debouncedData.length / 500))
        : 1;
      for (let i = 0; i < debouncedData.length; i += step) {
        for (const leg of activeLegends) {
          const v = Number(debouncedData[i][leg.dataKey]);
          if (!isNaN(v) && v > max) max = v;
        }
      }
      cachedMaxValue = max;
      return max;
    };

    if (enableClickSelection && debouncedData.length > 0) {
      const maxValue = getMaxValue();
      series.push({
        name: "__click_layer__",
        type: "bar",
        barGap: "-100%",
        data: debouncedData.map(() => maxValue * 1.2),
        itemStyle: { color: "transparent" },
        z: 100,
        cursor: "pointer",
        yAxisIndex: 0,
        legendHoverLink: false,
        animation: false,
      } as any);
    }

    if (
      highlightedXValues &&
      highlightedXValues.length > 0 &&
      debouncedData.length > 0
    ) {
      const maxValue = getMaxValue();
      const highlightSet = new Set(highlightedXValues.map(String));
      series.push({
        name: "__highlight_layer__",
        type: "bar",
        barGap: "-100%",
        data: debouncedData.map((item) =>
          highlightSet.has(String(item[xDataKey])) ? maxValue * 1.2 : 0,
        ),
        itemStyle: { color: "rgba(238, 244, 254, 0.75)" },
        z: 10,
        silent: true,
        yAxisIndex: 0,
        legendHoverLink: false,
        animation: false,
      } as any);
    }

    // Critical period bands — index-aligned full-height bar series on a
    // dedicated hidden yAxis (index 2, min:0 max:1) so band height never
    // affects the data y-axis scale. Same index-aligned pattern as
    // __highlight_layer__ so multi-day duplicate x-values work correctly.
    if (criticalPeriodBands && criticalPeriodBands.length > 0) {
      criticalPeriodBands.forEach((band, bi) => {
        series.push({
          id: `__cp_${band.kind}__`,
          name: `__cp_${band.kind}__`,
          type: "bar",
          barGap: "-100%",
          barCategoryGap: "0%",
          data: band.pointFill.map((fill) => (fill ? 1 : 0)),
          itemStyle: {
            color: (params: any) => {
              if (!band.pointFill[params.dataIndex]) return "transparent";
              return {
                type: "linear",
                x: 0, y: 0, x2: 0, y2: 1,
                colorStops: band.gradientStops,
              };
            },
            borderColor: "transparent",
          },
          z: 2 + bi,
          silent: true,
          yAxisIndex: 2,
          legendHoverLink: false,
          animation: false,
        } as any);
      });
    }

    const safeLen = debouncedData.length || 1;
    const lastIdx = Math.max(0, debouncedData.length - 1);
    const windowSize = Math.min(96, debouncedData.length);

    const defaultStartIdx =
      brushIndex?.startIndex !== undefined
        ? brushIndex.startIndex
        : Math.max(0, debouncedData.length - windowSize);
    const defaultEndIdx =
      brushIndex?.endIndex !== undefined ? brushIndex.endIndex : lastIdx;

    const epsilon = safeLen > 0 ? 0.5 / safeLen : 0;
    const defaultStartPercent = Math.min(
      100,
      (defaultStartIdx / safeLen) * 100 + epsilon * 100,
    );
    const defaultEndPercent = Math.min(
      100,
      ((defaultEndIdx + 1) / safeLen) * 100 - epsilon * 100,
    );

    // Slider position owned by ECharts during drag. React state (zoomRange)
    // only feeds smart-interval recompute below; do NOT push it back into
    // dataZoom.start/end or React fights ECharts mid-drag → jitter.
    const zoomStart = lastZoomRef.current?.start ?? defaultStartPercent;
    const zoomEnd = lastZoomRef.current?.end ?? defaultEndPercent;
    const visibleStart = zoomRange?.start ?? zoomStart;
    const visibleEnd = zoomRange?.end ?? zoomEnd;

    const dataZoomXAxisIndex = secondXDataKey ? [0, 1] : [0];

    const visibleFraction = Math.max(0.01, (visibleEnd - visibleStart) / 100);
    const visibleDataCount = Math.ceil(debouncedData.length * visibleFraction);

    const computeSmartXInterval = (): number | "auto" => {
      if (xAxisInterval !== undefined) return xAxisInterval;
      if (isTimeSlot) return "auto";
      if (visibleDataCount <= 15) return "auto";
      if (visibleDataCount <= 30) return Math.floor(visibleDataCount / 8) - 1;
      if (visibleDataCount <= 60) return Math.floor(visibleDataCount / 8) - 1;
      if (visibleDataCount <= 90) return Math.floor(visibleDataCount / 10) - 1;
      if (visibleDataCount <= 180) return Math.floor(visibleDataCount / 12) - 1;
      if (visibleDataCount <= 270) return Math.floor(visibleDataCount / 12) - 1;
      if (visibleDataCount <= 365) return Math.floor(visibleDataCount / 12) - 1;
      if (visibleDataCount <= 450) return Math.floor(visibleDataCount / 12) - 1;
      return Math.floor(visibleDataCount / 12) - 1;
    };

    const smartInterval = computeSmartXInterval();
    const lastXIndex = Math.max(0, xAxisData.length - 1);
    const xAxisIntervalFn =
      typeof smartInterval === "number"
        ? (index: number) => {
            if (index === 0 || index === lastXIndex) return true;
            return index % (smartInterval + 1) === 0;
          }
        : smartInterval;

    const secondXAxisConfig = secondXDataKey
      ? buildSecondXAxisConfig({
          debouncedData,
          secondXDataKey,
          zoomStart: visibleStart,
          zoomEnd: visibleEnd,
          fontSize,
          labelFontSize,
        })
      : [];

    const built: any = {
      animation: !isLarge,
      ...(isLarge ? { progressive: 3000, progressiveThreshold: 5000 } : {}),
      grid: {
        left: left ?? 40,
        right: 50,
        bottom: showBrush
          ? brushHeight + xaxisHeight - (isSmallLegend ? 30 : 10)
          : xaxisHeight - bottom,
        top: top || 20,
        containLabel: true,
      },
      tooltip: {
        trigger: "axis",
        triggerOn: "mousemove",
        // ECharts bug: when alwaysShowContent=true + appendTo:'body', every
        // setOption triggers TooltipHTMLContent._moveIfResized() which calls
        // moveTo() with ALREADY-OFFSET coords from _styleCoord. moveTo's
        // makeStyleCoord then adds chartRect AGAIN → stacks +chartRect per
        // option update → tooltip drifts/oscillates during zoom. Force off
        // for viewport mode; chart-mode has no offset so safe to keep on.
        alwaysShowContent:
          tooltipBoundary === "chart" ? !hideTooltipOnChartExit : false,
        axisPointer: {
          type: "line",
          snap: false,
          label: { show: false },
          xAxisIndex: 0,
          animation: false,
          triggerTooltip: true,
        },
        formatter: customTooltip || tooltipFormatter,
        enterable: !hideTooltipOnChartExit,
        confine: false,
        // boundary="chart": keep tooltip inside chart DOM so coords are
        // chart-relative and clamp uses chartW/chartH directly.
        // boundary="viewport": append to body so tooltip can escape chart
        // bounds; clamp converts viewport limits via chartRect.
        ...(tooltipBoundary === "chart" ? {} : { appendTo: "body" as any }),
        position: (
          point: number[],
          _params: any,
          dom: any,
          _rect: any,
          size: { contentSize: [number, number]; viewSize: [number, number] },
        ) => {
          const [px, py] = point;
          // ECharts intermittently calls position() with [0,0] contentSize during
          // notMerge setOption (e.g. on zoom). A static fallback (320×150) makes
          // the below/above flip-decision toggle between renders → tooltip
          // oscillates between two Y positions. Cache the last-known content
          // size on the DOM node and reuse it when ECharts passes 0 so the
          // flip decision stays stable mid-zoom.
          const rawCw = size.contentSize[0];
          const rawCh = size.contentSize[1];
          const cached = tooltipSizeRef.current;
          const cw = rawCw || cached?.cw || 320;
          const ch = rawCh || cached?.ch || 150;
          if (rawCw && rawCh) {
            tooltipSizeRef.current = { cw: rawCw, ch: rawCh };
          }
          const [chartW, chartH] = size.viewSize;
          const pad = 8;
          // Chart container rect in page coords — used only when tooltip is
          // body-appended (viewport mode) to convert viewport limits into
          // chart-local coords (ECharts always passes chart-relative coords).
          const chartRect: { left: number; top: number } | null =
            tooltipBoundary === "chart"
              ? null
              : (() => {
                  try {
                    // Prefer caller-supplied chart DOM rect — when tooltip is
                    // body-appended, dom.parentElement is body, so we cannot
                    // derive chart position from the tooltip element itself.
                    const r = getChartRect?.();
                    if (r) return r;
                    const root: any = dom?.parentElement;
                    const candidate =
                      root?.querySelector?.("canvas")?.parentElement;
                    const el = candidate || root;
                    return el?.getBoundingClientRect?.() ?? null;
                  } catch {
                    return null;
                  }
                })();
          const maxX =
            tooltipBoundary === "chart"
              ? chartW - pad
              : chartRect
                ? window.innerWidth - chartRect.left
                : window.innerWidth;
          const maxY =
            tooltipBoundary === "chart"
              ? chartH - pad
              : chartRect
                ? window.innerHeight - chartRect.top
                : window.innerHeight;
          const minX =
            tooltipBoundary === "chart"
              ? pad
              : chartRect
                ? -chartRect.left + pad
                : pad;
          const minY =
            tooltipBoundary === "chart"
              ? pad
              : chartRect
                ? -chartRect.top + pad
                : pad;
          
          const mode = dom?.getAttribute?.("data-mode") || "preview";
          const isInteractive = mode === "interactive" || mode === "pinned";

          // Keep tooltip away from cursor to avoid hover in/out flicker.
          const offsetX = isInteractive ? 22 : 32;
          const offsetY = isInteractive ? 18 : 24;
          const spaceRight = maxX - px;
          const spaceLeft = px - minX;
          const canPlaceRight = spaceRight >= cw + offsetX + pad;
          const canPlaceLeft = spaceLeft >= cw + offsetX + pad;
          const previousSide = tooltipSideRef.current;
          let placeRight = true;

          if (canPlaceRight && canPlaceLeft) {
            // Hysteresis prevents rapid left/right side flipping near edges.
            const hysteresis = 22;
            if (
              previousSide === "right" &&
              spaceRight + hysteresis >= cw + offsetX + pad
            ) {
              placeRight = true;
            } else if (
              previousSide === "left" &&
              spaceLeft + hysteresis >= cw + offsetX + pad
            ) {
              placeRight = false;
            } else {
              placeRight = spaceRight >= spaceLeft;
            }
          } else if (!canPlaceRight && canPlaceLeft) {
            placeRight = false;
          }

          let x = placeRight ? px + offsetX : px - cw - offsetX;
          if (x + cw + pad > maxX) x = Math.max(minX, maxX - cw - pad);
          if (x < minX) x = minX;

          // Prefer below cursor; flip above if would overflow bottom.
          let y = py + offsetY;
          if (y + ch + pad > maxY) {
            const upY = py - ch - offsetY;
            // Flip above if room exists, else hug top edge so tooltip stays
            // fully visible (never clipped past viewport bottom).
            y = upY >= minY ? upY : minY;
          }
          if (y < minY) y = minY;
          // Final hard clamp — if tooltip still taller than available space,
          // shift up so its bottom sits at maxY.
          if (y + ch + pad > maxY) y = Math.max(minY, maxY - ch - pad);
          // Track which side the hysteresis chose so the next render's
          // hysteresis sees a stable state. Don't let the overlap rescue
          // below overwrite it — that creates a side-flip loop across renders.
          const hysteresisSide: "left" | "right" = placeRight ? "right" : "left";
          const overlapsPointer =
            px >= x - 2 && px <= x + cw + 2 && py >= y - 2 && py <= y + ch + 2;
          if (overlapsPointer) {
            const oppositeX = placeRight ? px - cw - offsetX : px + offsetX;
            if (oppositeX >= minX && oppositeX + cw + pad <= maxX) {
              x = oppositeX;
            }
          }
          const finalPos = [Math.round(x), Math.round(y)];
          tooltipSideRef.current = hysteresisSide;
          if (dom) {
            (dom as any).__lastPos = finalPos;
          }
          return finalPos;
        },
        className: "remix-tt",
        // Do NOT set appendToBody at all. The modern `appendTo: 'body'` flag
        // (set above for viewport mode) handles body-appending correctly with
        // chart-relative coords from position(). Legacy `appendToBody: true`
        // alongside modern `appendTo: 'body'` made ECharts compound the
        // chart→page offset on every notMerge reinit during zoom, stacking
        // +chartRect.top per step → tooltip drifted offscreen, briefly
        // re-appeared at wrong location, looked like position-flicker.
        backgroundColor: "transparent",
        borderColor: "transparent",
        borderWidth: 0,
        borderRadius: 0,
        padding: 0,
        textStyle: { fontSize, fontWeight: 500, color: "#0f172a" },
        extraCssText:
          "max-height:calc(100vh - 120px);overflow:visible;padding:0;z-index:99999;transition:opacity 120ms ease;box-shadow:none !important;border:none !important;",
        showDelay: 0,
        hideDelay: 50,
        transitionDuration: 0,
      },
      legend: {
        show: false,
        data: series
          .filter((s: any) => !String(s.name).startsWith("__"))
          .map((s: any) => s.name),
      },
      toolbox: {
        show: showToolbox ?? true,
        top: -5,
        feature: {
          dataZoom: { yAxisIndex: "none" },
          restore: {},
          saveAsImage: {},
        },
      },
      xAxis: [
        {
          type: "category",
          data: xAxisData,
          axisLabel: {
            fontSize,
            rotate: xAxisAngle || 0,
            margin: xAxisTickMargin + 4,
            color: "#8C8A96",
            interval: xAxisIntervalFn,
            formatter: (value: string) =>
              typeof value === "string"
                ? (xAxisTickFormatter ? xAxisTickFormatter(value) : normalizeDisplayLabel(value))
                : value,
            hideOverlap: false,
            showMinLabel: true,
            showMaxLabel: true,
          },
          axisLine: { lineStyle: { color: "#E0E0E0" } },
          axisTick: {
            show: true,
            alignWithLabel: true,
            length: 4,
            lineStyle: { color: "#E0E0E0" },
          },
          name: xLabel,
          nameLocation:
            xAxisLabelPosition === "insideBottom"
              ? "middle"
              : xAxisLabelPosition,
          nameGap: xAxisLabelOffset || 15,
          nameTextStyle: {
            fontSize: labelFontSize ?? fontSize + 3,
            color: SecondaryColor,
          },
        },
        ...secondXAxisConfig,
      ],
      yAxis: [
        {
          type: "value",
          ...(yMaxValue ? { max: yMaxValue } : {}),
          splitNumber: 5,
          axisLabel: {
            fontSize,
            margin: 8,
            formatter: formatAxisValue,
            hideOverlap: true,
          },
          axisTick: { show: true, length: 4, lineStyle: { color: "#E0E0E0" } },
          scale: false,
          boundaryGap: [0, 0.1],
          name: yAxisLabel,
          nameLocation: "middle",
          nameGap: nameGap ?? 50,
          nameTextStyle: {
            fontSize: labelFontSize ?? fontSize,
            color: SecondaryColor,
            fontWeight: 600,
            fontFamily: "Inter, system-ui, sans-serif",
          },
        },
        {
          type: "value",
          position: "right",
          ...(secondYAxisDomain
            ? { min: secondYAxisDomain[0], max: secondYAxisDomain[1] }
            : {}),
          alignTicks: true,
          splitNumber: 5,
          axisLabel: {
            fontSize,
            margin: 8,
            formatter: formatAxisValue,
            hideOverlap: true,
          },
          axisTick: { show: true, length: 4, lineStyle: { color: "#E0E0E0" } },
          scale: false,
          boundaryGap: [0, 0.1],
          name: secondYAxisLabel,
          nameLocation: "middle",
          nameGap: nameGap ?? 50,
          nameTextStyle: {
            fontSize: labelFontSize ?? fontSize,
            color: SecondaryColor,
            fontWeight: 600,
            fontFamily: "Inter, system-ui, sans-serif",
          },
        },
        // Hidden yAxis for critical-period bands. min:0 max:1 keeps bands
        // at full chart height without affecting the data y-axis scale.
        {
          type: "value",
          min: 0,
          max: 1,
          show: false,
          axisLabel: { show: false },
          axisTick: { show: false },
          axisLine: { show: false },
          splitLine: { show: false },
        },
      ],
      series,
      dataZoom: [
        ...(showBrush
          ? [
              {
                type: "slider",
                start: zoomStart,
                end: zoomEnd,
                height: brushHeight,
                width: "98%",
                bottom: 10,
                left: 10,
                brushSelect: false,
                xAxisIndex: dataZoomXAxisIndex,
                textStyle: { color: SecondaryColor },
                handleIcon:
                  "path://M10.7,11.9v-1.3H9.3v1.3c-4.9,0.3-8.8,4.4-8.8,9.4c0,5,3.9,9.1,8.8,9.4v1.3h1.3v-1.3c4.9-0.3,8.8-4.4,8.8-9.4C19.5,16.3,15.6,12.2,10.7,11.9z M13.3,24.4H6.7V23h6.6V24.4z M13.3,19.6H6.7v-1.4h6.6V19.6z",
                handleSize: "80%",
                filterMode: "none" as const,
                throttle: isLarge ? 100 : 60,
              },
            ]
          : []),
        ...(enableMouseWheelZoom
          ? [
              {
                type: "inside",
                start: zoomStart,
                end: zoomEnd,
                xAxisIndex: dataZoomXAxisIndex,
                zoomLock: false,
                zoomOnMouseWheel: true,
                moveOnMouseMove: true,
                preventDefaultMouseMove: false,
                filterMode: "none" as const,
                throttle: isLarge ? 80 : 50,
              },
            ]
          : []),
      ],
    };
    lastOptionsRef.current = built;
    return built;
  }, [
    debouncedData,
    orderedSelectedLegends,
    selectedLegends,
    hiddenStats,
    colorMap,
    barCategoryGap,
    isPriceForecast,
    left,
    showBrush,
    brushHeight,
    xaxisHeight,
    isSmallLegend,
    bottom,
    top,
    customTooltip,
    tooltipFormatter,
    fontSize,
    showToolbox,
    xAxisData,
    xAxisAngle,
    xAxisTickMargin,
    xLabel,
    xAxisLabelPosition,
    xAxisLabelOffset,
    labelFontSize,
    secondXDataKey,
    yMaxValue,
    yAxisLabel,
    nameGap,
    secondYAxisLabel,
    brushIndex?.startIndex,
    brushIndex?.endIndex,
    enableMouseWheelZoom,
    secondYAxisDomain,
    buildSeries,
    colorByPoint,
    barColors,
    enableClickSelection,
    highlightedXValues,
    xDataKey,
    xAxisInterval,
    isTimeSlot,
    hideTooltipOnChartExit,
    zoomRange,
    lastZoomRef,
    tooltipBoundary,
    getChartRect,
    criticalPeriodBands,
    xAxisTickFormatter,
  ]);
}
